import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Layout from "../../components/layout/Layout";
import Dialog from "../../components/ui/Dialog";
import {
  useCreateSkill,
  useDeleteSkill,
  useSkills,
  useUpdateSkill,
} from "../../api/queries/learningQueries";
import { getApiErrorMessage, getApiErrorStatus } from "../../utils/apiError";
import "./learning.css";
import { useCanModify } from "../../hooks/usePermissions";

const EMPTY = [];

const SkillDialog = ({ skill, onClose }) => {
  const isEdit = !!skill;
  const [form, setForm] = useState({
    name: skill?.name || "",
    category: skill?.category || "",
    description: skill?.description || "",
  });
  const [error, setError] = useState(null);
  const create = useCreateSkill();
  const update = useUpdateSkill();
  const busy = create.isPending || update.isPending;

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError("Enter the skill name.");
    const body = {
      name: form.name.trim(),
      category: form.category.trim() || undefined,
      description: form.description.trim() || undefined,
    };
    const options = {
      onSuccess: () => {
        toast.success(isEdit ? "Skill updated." : "Skill added.");
        onClose();
      },
      onError: (err) =>
        setError(getApiErrorMessage(err, "Could not save the skill.")),
    };
    if (isEdit) update.mutate({ id: skill.id, ...body }, options);
    else create.mutate(body, options);
  };

  return (
    <Dialog
      title={isEdit ? "Edit skill" : "Add skill"}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="skill-form"
            className="btn btn-primary profile-btn"
            disabled={busy}
          >
            {busy && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            {isEdit ? "Save" : "Add skill"}
          </button>
        </>
      }
    >
      <form id="skill-form" onSubmit={submit} noValidate>
        {error && <div className="alert alert-danger fs-085 py-2">{error}</div>}
        <label htmlFor="skill-name" className="profile-label">
          Name <span className="text-danger">*</span>
        </label>
        <input
          id="skill-name"
          className="form-control"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Excel – Pivot tables"
          autoFocus
        />
        <label htmlFor="skill-category" className="profile-label mt-3">
          Category
        </label>
        <input
          id="skill-category"
          className="form-control"
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          placeholder="e.g. Technical, Leadership, Compliance"
        />
        <label htmlFor="skill-desc" className="profile-label mt-3">
          Description
        </label>
        <textarea
          id="skill-desc"
          className="form-control"
          rows={2}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </form>
    </Dialog>
  );
};

/** Delete; if the skill is used (409), offer to deactivate it instead. */
const DeleteSkillDialog = ({ skill, onClose }) => {
  const [inUse, setInUse] = useState(null);
  const remove = useDeleteSkill();
  const update = useUpdateSkill();
  const busy = remove.isPending || update.isPending;

  const doDelete = () =>
    remove.mutate(skill.id, {
      onSuccess: () => {
        toast.success(`${skill.name} deleted.`);
        onClose();
      },
      onError: (err) => {
        if (getApiErrorStatus(err) === 409)
          setInUse(
            getApiErrorMessage(err, "This skill is linked to seminars."),
          );
        else {
          toast.error(getApiErrorMessage(err, "Could not delete the skill."));
          onClose();
        }
      },
    });
  const deactivate = () =>
    update.mutate(
      { id: skill.id, isActive: false },
      {
        onSuccess: () => {
          toast.success(`${skill.name} deactivated.`);
          onClose();
        },
        onError: (err) =>
          toast.error(
            getApiErrorMessage(err, "Could not deactivate the skill."),
          ),
      },
    );

  return (
    <Dialog
      title={inUse ? "This skill is in use" : `Delete “${skill.name}”?`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          {inUse ? (
            <button
              type="button"
              className="btn btn-warning profile-btn"
              onClick={deactivate}
              disabled={busy || skill.isActive === false}
            >
              Deactivate instead
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-danger profile-btn"
              onClick={doDelete}
              disabled={busy}
            >
              Delete
            </button>
          )}
        </>
      }
    >
      <p className="fs-085 mb-0">
        {inUse
          ? `${inUse} Deactivating keeps past records but hides it for new seminars.`
          : "This can't be undone."}
      </p>
    </Dialog>
  );
};

/** Admin/HR: skills that seminars build (shown on employees' learning records). */
const SkillsPage = () => {
  // Learning: view-only roles see the catalog only
  const canModify = useCanModify("learning", { company: true });
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState(null); // {} = new
  const [deleting, setDeleting] = useState(null);
  const update = useUpdateSkill();

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchText.trim()), 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const query = useSkills({ search, includeInactive });
  const skills = query.data || EMPTY;

  const toggleActive = (s) =>
    update.mutate(
      { id: s.id, isActive: s.isActive === false },
      {
        onSuccess: () =>
          toast.success(
            `${s.name} ${s.isActive === false ? "activated" : "deactivated"}.`,
          ),
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not update the skill.")),
      },
    );

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0 page-title">Skills Catalog</h1>
            <p className="text-muted small mb-0">
              Skills that seminars build; attended seminars add them to the
              employee's learning record.
            </p>
          </div>
          {canModify && (
            <button
              type="button"
              className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
              onClick={() => setEditing({})}
            >
              <i className="bi bi-plus-lg me-2"></i> Add skill
            </button>
          )}
        </div>

        <div className="learn-filters mb-3">
          <div className="position-relative learn-filter-search">
            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
            <input
              type="search"
              className="filter-input w-100 ps-5"
              placeholder="Search skills"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              aria-label="Search skills"
            />
          </div>
          <div className="form-check form-switch m-0">
            <input
              id="skills-inactive"
              type="checkbox"
              className="form-check-input"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            <label
              htmlFor="skills-inactive"
              className="form-check-label fs-080"
            >
              Show inactive
            </label>
          </div>
        </div>

        <div className="glass-table-card">
          {query.isError ? (
            <div className="approval-empty text-danger">
              <i className="bi bi-exclamation-triangle"></i>
              {getApiErrorMessage(query.error, "Could not load skills.")}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table mb-0 learn-table">
                <thead className="custom-table-header">
                  <tr>
                    <th className="ps-4 py-3">Skill</th>
                    <th className="py-3">Category</th>
                    <th className="py-3">Description</th>
                    <th className="py-3">Active</th>
                    <th className="py-3 pe-4 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {skills.map((s) => (
                    <tr
                      key={s.id}
                      className={
                        s.isActive === false ? "learn-row-inactive" : ""
                      }
                    >
                      <td className="ps-4 fw-semibold">{s.name}</td>
                      <td>
                        {s.category ? (
                          <span className="chip chip--sm">{s.category}</span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="text-slate-600">{s.description || "—"}</td>
                      <td>
                        <div className="form-check form-switch m-0">
                          <input
                            type="checkbox"
                            role="switch"
                            className="form-check-input"
                            checked={s.isActive !== false}
                            onChange={() => toggleActive(s)}
                            disabled={update.isPending || !canModify}
                            aria-label={`${s.name} active`}
                          />
                        </div>
                      </td>
                      <td className="pe-4 text-end">
                        {canModify && (
                          <div className="d-inline-flex gap-1">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary btn-icon-28"
                              title="Edit"
                              aria-label={`Edit ${s.name}`}
                              onClick={() => setEditing(s)}
                            >
                              <i className="bi bi-pencil-square"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger btn-icon-28"
                              title="Delete"
                              aria-label={`Delete ${s.name}`}
                              onClick={() => setDeleting(s)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!query.isLoading && skills.length === 0 && (
                    <tr>
                      <td colSpan={5} className="approval-empty">
                        <i className="bi bi-stars"></i>
                        {search ? "No skills match." : "No skills yet."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {query.isLoading && (
                <div className="text-center py-5">
                  <span
                    className="spinner-border text-primary"
                    role="status"
                    aria-label="Loading"
                  ></span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <SkillDialog
          skill={editing.id ? editing : null}
          onClose={() => setEditing(null)}
        />
      )}
      {deleting && (
        <DeleteSkillDialog skill={deleting} onClose={() => setDeleting(null)} />
      )}
    </Layout>
  );
};

export default SkillsPage;
